import { z } from "zod";

// TES-1102 S3 (Emelie 2026-10-04, via TES-1093) — booking diagnostics: the
// people who started booking their first care meeting (opened the payment
// step, or chose "Boka vårdmöte" on an early-stop result) and did not book.
//
// An internal admin list, per person: whether she can be contacted, and the
// reason staff recorded after talking to her. The onboarding funnel stays
// aggregated: it gains only a count per reason category, never a name.

/**
 * Emelie's drop-off categories, in her order. Her Swedish wording is the
 * label, in BOOKING_DROP_OFF_LABELS below.
 */
export const BOOKING_DROP_OFF_CATEGORIES = [
  "price_unexpected",
  "unsure_meeting_value",
  "unsure_future_costs",
  "wanted_to_think",
  "technical_problem",
  "time_or_availability",
  "personal_or_medical",
  "other",
] as const;
export const bookingDropOffCategorySchema = z.enum(BOOKING_DROP_OFF_CATEGORIES);
export type BookingDropOffCategory = z.infer<
  typeof bookingDropOffCategorySchema
>;

/** Emelie's own words (TES-1102), shown in the admin list and the funnel. */
export const BOOKING_DROP_OFF_LABELS: Record<BookingDropOffCategory, string> = {
  price_unexpected: "Priset kom oväntat",
  unsure_meeting_value: "Osäker på värdet av vårdmötet",
  unsure_future_costs: "Osäker på kommande kostnader",
  wanted_to_think: "Ville fundera",
  technical_problem: "Tekniskt problem",
  time_or_availability: "Tid eller tillgänglighet",
  personal_or_medical: "Personlig eller medicinsk anledning",
  other: "Annat",
};

export const BOOKING_DROP_OFF_NOTE_MAX_LENGTH = 2000;

/** Where she got to before she stopped. */
export const BOOKING_REACHED_STEPS = [
  "payment_step",
  "booking_intent",
] as const;
export type BookingReachedStep = (typeof BOOKING_REACHED_STEPS)[number];

// One person in the list. `contactable` (TES-1106) means Ella may contact
// her for product feedback: she has granted the product-feedback consent AND
// has a verified e-mail. Before TES-1106 there was no contact consent at all
// and the flag was app use plus no support opt-out; those stay as their own
// columns, so the list still says how she can be reached and WHY someone is
// not contactable.
export const bookingDropOffRowSchema = z.object({
  patientId: z.string(),
  patientName: z.string(),
  /** When she first reached the step (ISO). */
  reachedAt: z.string(),
  reached: z.enum(BOOKING_REACHED_STEPS),
  emailVerified: z.boolean(),
  /** She has used the app, so she can read the support thread. */
  reachableInApp: z.boolean(),
  /** She has turned off Ella Support's messages. */
  optedOut: z.boolean(),
  /** TES-1106 — her product-feedback consent is granted right now. */
  feedbackConsent: z.boolean(),
  contactable: z.boolean(),
  /** The support thread has at least one message. */
  supportThreadStarted: z.boolean(),
  /** The recorded reason. The note itself is never in the list. */
  reason: z
    .object({
      category: bookingDropOffCategorySchema,
      hasNote: z.boolean(),
      recordedAt: z.string(),
    })
    .nullable(),
});
export type BookingDropOffRow = z.infer<typeof bookingDropOffRowSchema>;

export const bookingDropOffListSchema = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(bookingDropOffRowSchema),
});
export type BookingDropOffList = z.infer<typeof bookingDropOffListSchema>;

// PUT employee-api/v1/booking-diagnostics/patients/:patientId/drop-off-reason
// Trimmed; an empty note is no note.
export const setBookingDropOffReasonSchema = z.object({
  category: bookingDropOffCategorySchema,
  note: z
    .string()
    .trim()
    .max(BOOKING_DROP_OFF_NOTE_MAX_LENGTH)
    .nullable()
    .optional(),
});
export type SetBookingDropOffReason = z.infer<
  typeof setBookingDropOffReasonSchema
>;

// GET (and the PUT's answer): the reason with its note, for the staff form.
export const bookingDropOffReasonSchema = z.object({
  category: bookingDropOffCategorySchema,
  note: z.string().nullable(),
  recordedAt: z.string(),
});
export type BookingDropOffReason = z.infer<typeof bookingDropOffReasonSchema>;

// The funnel's aggregate: one count per category, every category listed.
export const bookingDropOffCountsSchema = z.array(
  z.object({
    category: bookingDropOffCategorySchema,
    count: z.number().int().nonnegative(),
  }),
);
export type BookingDropOffCounts = z.infer<typeof bookingDropOffCountsSchema>;
